import { useContext, useEffect, useMemo, useState } from "react";
import {
  useCreateCalendarIntegration,
  useGetAdvisorCalendarIntegrations,
  useGetMemberCalendarIntegrations,
  useUpdateCalendarIntegration,
} from "services/calendar";
import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import Breadcrumbs from "components/common/Breadcrumbs";
import useIsMobile from "components/hooks/useIsMobile";
import Loading from "components/common/Loading";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router";
import SnackPack, { SnackbarMessage } from "../components/common/SnackPack";
import CalendarFlow from "components/calendar/integrations/ImportCalendarFlow";
import ExportCalendar from "components/calendar/integrations/ExportCalendarFlow";
import DropdownMenu from "components/common/DropdownMenu";
import {
  EXPORT_CALENDARS,
  ExportedCalendar,
} from "components/calendar/integrations/exports";
import {
  MEMBER_IMPORT_CALENDARS,
  ADVISOR_IMPORT_CALENDARS,
  ImportCalendar,
} from "components/calendar/integrations/imports";
import ImportedIntegration from "components/calendar/integrations/ImportedIntegration";
import ExportedIntegration from "components/calendar/integrations/ExportedIntegration";
import { CalendarIntegration } from "protogen/calendar_service_pb";
import { REDIRECT_URI } from "components/calendar/integrations/constants";
import { useUpdateFamily } from "services/member";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import { CurrentUserContext } from "components/context/RequireAuth";
import { planFlagsFromStatus } from "utils/memberFamilyPlan";
import { calendarImportedCalendarsExplainer } from "common/memberModuleResolver";
import { UpdateFamilyRequest } from "protogen/advisors_service_pb";
import { useTwoWaySyncEnabled } from "components/calendar/useTwoWaySyncEnabled";
import GroupedCheckedDropDown, {
  Option,
} from "components/common/GroupedCheckedDropDown";
import DefaultCalendarSelectionModal from "components/calendar/integrations/DefaultCalendarSelectionModal";

type IntegrationResponse = {
  type: string;
  code?: string;
  scope?: string;
  redirectUri?: string;
  calendarUrl?: string;
  state?: string;
};

export default ({ accountType }: { accountType: "member" | "advisor" }) => {
  let params = useParams();
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const isTwoWaySyncEnabled = useTwoWaySyncEnabled();
  const [integrationSuccess, setIntegrationSuccess] = useState(
    searchParams.get("calendarAdded") === "success",
  );
  const [exportType, setExportType] = useState<ExportedCalendar | null>(null);
  const [importType, setImportType] = useState<ImportCalendar | null>(null);
  const [integrationResponse, setIntegrationResponse] =
    useState<IntegrationResponse | null>(null);
  const [selectedCalendarValue, setSelectedCalendarValue] = useState<
    string | null
  >(null);
  const [showDefaultCalendarModal, setShowDefaultCalendarModal] =
    useState(false);
  const getIntegrationsFn =
    accountType === "advisor"
      ? useGetAdvisorCalendarIntegrations
      : useGetMemberCalendarIntegrations;
  const { request, loading, data } = getIntegrationsFn();
  const [snackPack, setSnackPack] = useState<readonly SnackbarMessage[]>([]);
  const {
    request: createRequest,
    error: createError,
    loading: createLoading,
  } = useCreateCalendarIntegration();
  const { request: updateRequest } = useUpdateCalendarIntegration();
  const { data: memberDetails, refresh: getMemberDetails } = useMemberDetails();
  const currentUser = useContext(CurrentUserContext);
  const { isDigitalSubscription, isUnpaidSignup, familyStatus } =
    planFlagsFromStatus(currentUser.familyStatus);
  const { request: updateFamilyRequest } = useUpdateFamily(() => {
    setSnackPack((p) => [
      ...p,
      {
        message: "Default calendar updated!",
        saveNotification: true,
        key: new Date().getTime(),
      },
    ]);
    if (accountType === "member") {
      getMemberDetails();
    }
  });

  // Reset local selection when memberDetails updates
  useEffect(() => {
    setSelectedCalendarValue(null);
  }, [
    memberDetails?.family?.defaultCalendarIntegrationRef,
    memberDetails?.family?.defaultCalendarId,
  ]);

  // Show default calendar modal for new two-way sync integrations added before
  // this render, such as after returning to this page with calendarAdded=success.
  useEffect(() => {
    if (
      integrationSuccess &&
      data?.importIntegrations?.length &&
      accountType === "member" &&
      isTwoWaySyncEnabled
    ) {
      const mostRecentIntegration = data.importIntegrations.sort(
        (a, b) => Number(a.createdSec) - Number(b.createdSec),
      )[data.importIntegrations.length - 1];

      if (mostRecentIntegration.accessScope === "read_write") {
        setShowDefaultCalendarModal(true);
      }
    }
  }, [integrationSuccess, data, accountType, isTwoWaySyncEnabled]);
  // Should move this somewhere else and manage different callbacks differently.
  useEffect(() => {
    setIntegrationSuccess(searchParams.get("calendarAdded") === "success");
  }, [searchParams]);
  useEffect(() => {
    if (params.calendarType === "google") {
      const code = searchParams.get("code");
      const scope = searchParams.get("scope");
      const state = searchParams.get("state");
      if (code) {
        setIntegrationResponse({
          code,
          scope: scope || undefined,
          redirectUri: REDIRECT_URI,
          type: "google",
          state: state || undefined,
        });
      }
    }
    request();
    if (accountType === "member") {
      getMemberDetails();
    }
  }, [params.calendarType]);

  const doToast = (key: number) => {
    setSnackPack((p) => [
      ...p,
      {
        message: "Changes saved!",
        saveNotification: true,
        key: `${key}`,
      },
    ]);
  };

  const clearCalendarAddedSuccess = () => {
    setIntegrationSuccess(false);
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("calendarAdded");
    navigate(
      {
        pathname: location.pathname,
        search: nextParams.toString() ? `?${nextParams.toString()}` : "",
      },
      { replace: true },
    );
  };

  // Should move this somewhere else and manage different callbacks differently.
  useEffect(() => {
    const create = async (ir: IntegrationResponse) => {
      let stateData: { replaceIntegrationRef?: string; returnTo?: string } = {};
      if (ir.state) {
        try {
          stateData = JSON.parse(ir.state);
        } catch (_error) {}
      }

      const createResp = await createRequest({
        calendarType: ir.type,
        accessCode: ir.code || "",
        accessScope: ir.scope || "",
        accessRedirectUri: ir.redirectUri || "",
        calendarUrl: ir.calendarUrl || "",
        familyRef: "",
      });
      if (!createResp) {
        return;
      }

      // If upgrading (replacing an old integration), deactivate the old one
      if (stateData.replaceIntegrationRef) {
        await updateRequest({
          integrationRef: stateData.replaceIntegrationRef,
          deactivate: true,
          activate: false,
          enableCalendarId: "",
          disableCalendarId: "",
        });
      }

      if (stateData.returnTo) {
        navigate(`${stateData.returnTo}?calendarAdded=success`);
        request();
        if (accountType === "member") {
          getMemberDetails();
        }
        return;
      }

      navigate(
        {
          pathname: window.location.pathname,
          search: `?calendarAdded=success`,
        },
        { replace: true },
      );
      request();
      if (accountType === "member") {
        getMemberDetails();
      }
    };
    if (integrationResponse) {
      create(integrationResponse);
    }
  }, [integrationResponse]);
  let openIntegration: CalendarIntegration | null = null;
  if (integrationSuccess && data?.importIntegrations?.length) {
    openIntegration = data.importIntegrations.sort(
      (a, b) => Number(a.createdSec) - Number(b.createdSec),
    )[data.importIntegrations.length - 1];
  }

  const calendarDropdownData = useMemo(() => {
    if (!memberDetails?.family) return null;

    const twoWaySyncIntegrations = (data?.importIntegrations || []).filter(
      (integration: CalendarIntegration) =>
        integration.accessScope === "read_write" &&
        integration.status === "active",
    );

    const storedValue =
      memberDetails.family.defaultCalendarIntegrationRef &&
      memberDetails.family.defaultCalendarId
        ? `${memberDetails.family.defaultCalendarIntegrationRef}:${memberDetails.family.defaultCalendarId}`
        : "faye";

    // Check if value exists - helper function
    const isValidValue = (value: string, groupsList: any[]) => {
      return groupsList.some((group) =>
        group.options.some((opt: any) => opt.value === value),
      );
    };

    // Build groups structure first (without defaultChecked)
    const groupsStructure = [
      {
        label: "Faye",
        options: [{ label: "Faye calendar", value: "faye" }],
      },
      ...twoWaySyncIntegrations.map((integration: CalendarIntegration) => ({
        label: integration.name || "Calendar",
        integrationRef: integration.ref,
        options: (integration.calendars || [])
          .filter((cal: any) => cal.enabled && cal.role !== "reader")
          .map((cal: any) => ({
            key: `${integration.ref}:${cal.id}`,
            label: cal.summary,
            value: `${integration.ref}:${cal.id}`,
          })),
      })),
    ];

    // Validate current value
    const currentValue = isValidValue(storedValue, groupsStructure)
      ? storedValue
      : "faye";

    // Add defaultChecked to groups
    const groups: Array<{
      label: string;
      options: Option[];
      integrationRef?: string;
    }> = groupsStructure.map((group) => ({
      ...group,
      options: group.options.map((opt: any) => ({
        ...opt,
        defaultChecked: opt.value === currentValue,
      })),
    }));

    // Helper to find calendar info from value
    const findCalendarInfo = (value: string) => {
      if (value === "faye") {
        return { label: "Faye calendar", integrationRef: "", calendarId: "" };
      }

      for (const group of groups) {
        const option = group.options.find((opt) => opt.value === value);
        if (option) {
          const [integrationRef, calendarId] = value.split(":");
          return {
            label: option.label,
            integrationRef: integrationRef || "",
            calendarId: calendarId || "",
          };
        }
      }

      // Fallback to Faye if not found
      return { label: "Faye calendar", integrationRef: "", calendarId: "" };
    };

    const currentCalendar = findCalendarInfo(currentValue);

    return {
      currentValue,
      currentCalendar,
      groups,
      findCalendarInfo,
    };
  }, [memberDetails?.family, data?.importIntegrations]);

  return (
    <Box
      sx={{
        margin: isMobile ? "" : "0 auto",
        maxWidth: "850px",
        padding: isMobile ? "20px" : "64px",
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? "16px" : "",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box display="flex" flexDirection="column" width="100%">
          <Breadcrumbs
            breadcrumbs={[
              {
                name: "Home",
                link: "/",
              },
              { name: "Calendar", link: "/calendar" },
            ]}
          />
          <Typography
            variant="display"
            sx={{
              marginTop: "13px",
              marginBottom: "24px",
            }}
          >
            Calendar settings
          </Typography>
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Typography variant="h3">Imported calendars</Typography>
                <DropdownMenu
                  size="small"
                  noIcon={true}
                  fullWidth
                  buttonContent="Add a calendar"
                  options={(accountType === "advisor"
                    ? ADVISOR_IMPORT_CALENDARS
                    : MEMBER_IMPORT_CALENDARS
                  ).map((c) => ({
                    title: c.title,
                    onClick: () => setImportType(c),
                  }))}
                />
              </Box>
              <Typography variant="body">
                {accountType === "advisor"
                  ? "Import your calendars to help your Advisor see availability for appointments and spot more opportunities to help."
                  : calendarImportedCalendarsExplainer({
                      isDigitalSubscription,
                      isUnpaidSignup,
                      familyStatus,
                    })}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "16px",
          padding: !isMobile ? "12px 0" : undefined,
        }}
      >
        {!createLoading && integrationSuccess && !createError && (
          <Alert sx={{ display: "flex", alignItems: "center" }}>
            <AlertTitle sx={{ marginBottom: 0 }}>New calendar added</AlertTitle>
          </Alert>
        )}
        {integrationResponse && createError && (
          <Alert severity="error">
            {/* Error? */}
            <AlertTitle>Error Adding Calendar</AlertTitle>
            {createError}
          </Alert>
        )}

        {/* Default calendar dropdown */}
        {calendarDropdownData &&
          isTwoWaySyncEnabled &&
          accountType === "member" &&
          memberDetails?.family && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              {isMobile && (
                <Typography variant="bodyHeavy">Default calendar</Typography>
              )}

              <GroupedCheckedDropDown
                radio
                multiple={false}
                groups={calendarDropdownData.groups.map((group) => ({
                  label: group.label,
                  options: group.options,
                  onChange: (selected: Option[]) => {
                    if (!memberDetails?.family || !calendarDropdownData) return;

                    // For radio behavior: if something was selected, use it
                    // Otherwise, this group was deselected
                    if (selected.length === 0) return;

                    const value = selected[0].value;
                    setSelectedCalendarValue(value);

                    const calendarInfo =
                      calendarDropdownData.findCalendarInfo(value);

                    const request = new UpdateFamilyRequest({
                      familyRef: memberDetails.family.ref,
                      shouldUpdateDefaultCalendar: true,
                      updatedDefaultCalendarIntegrationRef:
                        calendarInfo.integrationRef,
                      updatedDefaultCalendarId: calendarInfo.calendarId,
                    });

                    updateFamilyRequest(request);
                  },
                }))}
                displayValue={
                  <Box display="flex" flexDirection="row" gap="4px">
                    {!isMobile && (
                      <Typography variant="body">Default calendar:</Typography>
                    )}
                    <Typography variant={isMobile ? "body" : "bodyHeavy"}>
                      {selectedCalendarValue
                        ? calendarDropdownData.findCalendarInfo(
                            selectedCalendarValue,
                          ).label
                        : calendarDropdownData.currentCalendar.label}
                    </Typography>
                  </Box>
                }
                sx={{ width: "fit-content" }}
              />
            </Box>
          )}
        {(data?.importIntegrations || []).map((i) => (
          <ImportedIntegration
            key={i.ref}
            integration={i}
            // Default new entries to open.
            defaultOpen={openIntegration?.ref === i.ref}
            onUpdate={() => {
              doToast(new Date().getTime());
              request();
            }}
          />
        ))}
        {!loading &&
          data?.importIntegrations.filter((i) => i.status !== "deactivated")
            .length === 0 && (
            <Typography
              variant="body"
              sx={{ color: "#6B6E7B", marginTop: isMobile ? "16px" : "" }}
            >
              No calendars connected
            </Typography>
          )}
        {integrationResponse && createLoading && <Loading />}
        {!createLoading && loading && !data && <Loading />}

        <Box
          display="flex"
          width="100%"
          marginTop="24px"
          gap={isMobile ? "12px" : "24px"}
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" flexDirection="column" gap="8px">
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Typography variant="h3">Faye calendar sync</Typography>
              <DropdownMenu
                size="small"
                noIcon={true}
                fullWidth
                buttonContent="Export"
                options={EXPORT_CALENDARS.map((c) => ({
                  title: c.title,
                  onClick: () => setExportType(c),
                }))}
              />
            </Box>
            <Typography variant="body">
              Connect your Faye calendar to view it in the calendar app of your
              choice, including Google Calendar, Apple Calendar, Outlook,
              Skylight and more.
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box>
        {(data?.exportIntegrations || []).length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {(data?.exportIntegrations || []).map(
              (int) =>
                int.status !== "deactivated" && (
                  <ExportedIntegration
                    key={int.ref}
                    link={int}
                    refresh={() => request()}
                  />
                ),
            )}
          </Box>
        )}
        {!loading &&
          data?.exportIntegrations.filter((i) => i.status !== "deactivated")
            .length === 0 && (
            <Typography
              variant="body"
              sx={{ color: "#6B6E7B", marginTop: isMobile ? "16px" : "" }}
            >
              No calendars connected
            </Typography>
          )}
      </Box>
      {importType && (
        <CalendarFlow
          importType={importType}
          open={!!importType}
          onClose={() => setImportType(null)}
          onSuccess={async () => {
            await request();
            setIntegrationSuccess(true);
            setImportType(null);
          }}
        />
      )}
      {exportType && (
        <ExportCalendar
          open={!!exportType}
          calendarType={exportType}
          onClose={(refresh: boolean) => {
            if (refresh) {
              request();
            }
            setExportType(null);
          }}
        />
      )}
      {showDefaultCalendarModal && memberDetails?.family && (
        <DefaultCalendarSelectionModal
          key={`${memberDetails.family.defaultCalendarIntegrationRef}:${memberDetails.family.defaultCalendarId}`}
          open={showDefaultCalendarModal}
          onClose={() => {
            setShowDefaultCalendarModal(false);
            clearCalendarAddedSuccess();
          }}
          onSelect={(integrationRef, calendarId) => {
            const request = new UpdateFamilyRequest({
              familyRef: memberDetails.family!.ref,
              shouldUpdateDefaultCalendar: true,
              updatedDefaultCalendarIntegrationRef: integrationRef,
              updatedDefaultCalendarId: calendarId,
            });
            updateFamilyRequest(request);
            setShowDefaultCalendarModal(false);
            clearCalendarAddedSuccess();
          }}
          integrations={data?.importIntegrations || []}
          currentDefaultIntegrationRef={
            memberDetails.family.defaultCalendarIntegrationRef
          }
          currentDefaultCalendarId={memberDetails.family.defaultCalendarId}
        />
      )}
      <SnackPack
        autoHideDuration={2000}
        snackPack={snackPack}
        alertSeverity="success"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
};
